import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Flame, Trophy, BookOpen, Puzzle, Zap, ArrowRight, Lock } from "lucide-react";
import api from "@/lib/api";
import { LEVEL_META } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

function StatCard({ icon: Icon, label, value, gold }) {
  return (
    <div className="rounded-xl border border-chessup-border bg-chessup-card p-4" data-testid={`stat-${label}`}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${gold ? "bg-chessup-gold/12 text-chessup-goldlight" : "bg-chessup-turq/12 text-chessup-turq"}`}><Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} /></div>
      <div className="text-2xl font-bold text-chessup-text">{value}</div>
      <div className="text-xs text-chessup-subtext mt-0.5">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/me/dashboard").then(({ data }) => setData(data)).catch(() => {}); }, []);

  if (!data) return <div className="p-8"><div className="h-8 w-48 bg-chessup-card rounded animate-pulse" /></div>;

  const meta = LEVEL_META[data.level_key] || LEVEL_META.pawn;
  const firstName = (data.name || "aluno").split(" ")[0];

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <h1 className="font-heading text-3xl text-chessup-text">Olá, {firstName}.</h1>
          <p className="text-chessup-subtext text-sm mt-1">Bom te ver de volta ao tabuleiro.</p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-chessup-gold/40 bg-chessup-gold/10 px-4 py-2">
          <span className="text-2xl">{meta.piece}</span>
          <div><div className="text-sm font-semibold text-chessup-goldlight">Nível {meta.name}</div><div className="text-xs text-chessup-subtext">{data.total_xp} XP</div></div>
        </div>
      </div>

      {!data.is_premium && (
        <div className="mb-6 rounded-xl border border-chessup-gold/40 bg-chessup-gold/8 p-5 flex items-center justify-between flex-wrap gap-3" data-testid="premium-banner">
          <div className="flex items-center gap-3"><Lock className="h-5 w-5 text-chessup-goldlight" /><span className="text-sm text-chessup-text">Libere todos os cursos, puzzles e o tabuleiro interativo completo.</span></div>
          <Link to="/assinatura" className="rounded-full bg-chessup-gold px-5 py-2 text-sm font-semibold text-chessup-deep hover:bg-chessup-goldlight">LIBERAR ACESSO COMPLETO</Link>
        </div>
      )}

      {/* Continue */}
      <div className="rounded-2xl border border-chessup-turq/30 bg-gradient-to-br from-chessup-card to-chessup-deep p-6 mb-8" data-testid="continue-card">
        <div className="text-xs font-mono uppercase tracking-widest text-chessup-turq mb-3">Continue de onde parou</div>
        {data.continue ? (
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-sm text-chessup-subtext">{data.continue.course_title} · {data.continue.module_title}</div>
              <div className="font-heading text-xl text-chessup-text mt-1">{data.continue.lesson_title}</div>
              {data.continue.last_position > 0 && <div className="text-sm text-chessup-turq mt-1">Continuar em {Math.floor(data.continue.last_position/60)}:{String(data.continue.last_position%60).padStart(2,"0")}</div>}
            </div>
            <Link to={`/aula/${data.continue.lesson_id}`} data-testid="continue-lesson-btn" className="inline-flex items-center gap-2 rounded-full bg-chessup-gold px-6 py-3 font-semibold text-chessup-deep hover:bg-chessup-goldlight">
              <Play className="h-4 w-4" fill="currentColor" /> CONTINUAR AULA
            </Link>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div><div className="font-heading text-xl text-chessup-text">Sua jornada começa agora</div><div className="text-sm text-chessup-subtext mt-1">Escolha um curso e dê seu primeiro lance.</div></div>
            <Link to="/cursos" className="inline-flex items-center gap-2 rounded-full bg-chessup-gold px-6 py-3 font-semibold text-chessup-deep hover:bg-chessup-goldlight"><BookOpen className="h-4 w-4" /> EXPLORAR CURSOS</Link>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Zap} label="XP total" value={data.total_xp} />
        <StatCard icon={BookOpen} label="Aulas concluídas" value={data.lessons_completed} />
        <StatCard icon={Puzzle} label="Puzzles resolvidos" value={data.puzzles_solved} />
        <StatCard icon={Flame} label="Sequência de estudos" value={`${data.streak} ${data.streak === 1 ? "dia" : "dias"}`} gold />
      </div>

      <div className="rounded-xl border border-chessup-border bg-chessup-card p-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3"><Trophy className="h-5 w-5 text-chessup-goldlight" /><div><div className="text-xs text-chessup-subtext uppercase tracking-wide">Próximo objetivo</div><div className="text-chessup-text mt-0.5">Resolva puzzles e conclua aulas para subir ao próximo nível.</div></div></div>
        <Link to="/puzzles" className="inline-flex items-center gap-1.5 text-sm text-chessup-turq hover:underline">Treinar puzzles <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </div>
  );
}
